import { ReconciliationSummary } from 'apis/reconciliation';
import { Box, SimpleGrid, Skeleton, Text } from 'design-system';
import { formatAed, formatVariance } from 'pages/reconciliation/utils/format';

interface IKpiCard {
  title: string;
  value: string;
  unit?: string;
  valueColor?: string;
  sub?: string;
  subColor?: string;
  isLoading?: boolean;
}

const KpiCard = ({ title, value, unit, valueColor, sub, subColor, isLoading }: IKpiCard) => (
  <Box bg='white' borderRadius='lg' boxShadow='0 1px 3px rgba(0,0,0,.06)' p='18px'>
    <Text fontSize='xs' fontWeight='semibold' color='gray.500' textTransform='uppercase' letterSpacing='0.4px'>
      {title}
    </Text>

    {isLoading ? (
      <Skeleton height='28px' width='100px' mt='8px' />
    ) : (
      <Text fontSize={{ base: '20px', md: '26px' }} fontWeight='bold' color={valueColor ?? 'gray.700'} mt='8px'>
        {value}
        {unit && (
          <Text as='span' fontSize='13px' fontWeight='semibold' color='gray.400'>
            {' '}
            {unit}
          </Text>
        )}
      </Text>
    )}

    {!isLoading && sub && (
      <Text fontSize='xs' color={subColor ?? 'gray.400'} mt='4px' noOfLines={2}>
        {sub}
      </Text>
    )}
  </Box>
);

interface IKpiCards {
  summary?: ReconciliationSummary;
  isLoading: boolean;
}

export const KpiCards = ({ summary, isLoading }: IKpiCards) => {
  const soaDelta = summary?.soaDelta ?? 0;
  const pendingCount = summary ? summary.suppliersCount - summary.soaEnteredCount : 0;
  const soaDeltaColor = soaDelta === 0 ? 'gray.400' : '#E03E52';
  // "(paid)" signals the delta's basis: the BE computes soaDelta from the effective SOA
  // (soaCredit ?? soaGmv per restaurant, "Paid wins when entered") while the card's headline
  // value is the debit total — without the label the two can look like they don't tie out
  // (e.g. platform 1,364 / SOA 1,364 / "+136.00 vs platform" when a credit differs from its debit).
  const soaDeltaSub = summary
    ? (soaDelta === 0 ? 'Matches platform' : `${formatVariance(soaDelta)} vs platform (paid)`) +
      (pendingCount > 0 ? ` · ${pendingCount} pending` : '')
    : undefined;
  const hasOutstanding = !!summary && summary.outstanding > 0;
  const invoiced = summary ? summary.collected + summary.outstanding : null;

  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing='md'>
      <KpiCard
        title='Total platform GMV'
        value={formatAed(summary?.platformGmv ?? null)}
        unit='AED'
        sub={summary ? `${summary.suppliersCount} suppliers · ${summary.restaurantsCount} restaurants` : undefined}
        isLoading={isLoading}
      />

      <KpiCard
        title='Total SOA GMV'
        value={formatAed(summary?.soaGmv ?? null)}
        unit='AED'
        sub={soaDeltaSub}
        subColor={soaDeltaColor}
        isLoading={isLoading}
      />

      <KpiCard
        title='Revenue'
        value={formatAed(summary?.revenue ?? null)}
        unit='AED'
        sub='Per-order commissions'
        isLoading={isLoading}
      />

      <KpiCard
        title='Collected'
        value={formatAed(summary?.collected ?? null)}
        unit='AED'
        valueColor='#2f9e22'
        sub='Commission collections'
        isLoading={isLoading}
      />

      <KpiCard
        title='Outstanding'
        value={formatAed(summary?.outstanding ?? null)}
        unit='AED'
        valueColor={hasOutstanding ? '#C53030' : '#2f9e22'}
        sub={invoiced !== null ? `of ${formatAed(invoiced)} AED invoiced` : undefined}
        isLoading={isLoading}
      />
    </SimpleGrid>
  );
};
